import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Chip, Button, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  TextField, MenuItem, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, LinearProgress, Stack, Divider, Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import BugReportIcon from '@mui/icons-material/BugReport';
import TerminalIcon from '@mui/icons-material/Terminal';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TimelineIcon from '@mui/icons-material/Timeline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import moment from 'moment-jalaali';
import { toast } from 'react-toastify';

import monitoringService, {
  Overview, SystemSnapshot, HttpMetrics, ErrorGroup, ErrorGroupDetail,
  AlertIncident, LogLine, UptimeReport, HealthSample, RouteMetric, ErrorStatus,
} from '../services/monitoringService';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import './EntityManagement.css';

// ---------------------------------------------------------------------------
// کمکی‌ها
// ---------------------------------------------------------------------------

/**
 * `toPersianDigits` فقط رشته می‌گیرد، ولی اینجا بیشتر مقادیر عددند (میلی‌ثانیه،
 * تعداد، درصد). این پوشش نازک از پخش‌شدن `String(...)` در کل فایل جلوگیری می‌کند.
 */
const pd = (value: unknown): string => toPersianDigits(String(value ?? ''));

const fmtDate = (iso?: string | null) => (iso ? pd(moment(iso).format('jYYYY/jMM/jDD HH:mm:ss')) : '—');
const fmtTime = (iso?: string | null) => (iso ? pd(moment(iso).format('HH:mm:ss')) : '—');
const fmtNum = (n?: number | null) => (n == null ? '—' : pd(n.toLocaleString('fa-IR')));

const fmtDuration = (sec?: number | null) => {
  if (sec == null) return '—';
  if (sec < 60) return `${pd(Math.round(sec))} ثانیه`;
  if (sec < 3600) return `${pd(Math.floor(sec / 60))} دقیقه`;
  if (sec < 86400) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return m ? `${pd(h)} ساعت و ${pd(m)} دقیقه` : `${pd(h)} ساعت`;
  }
  return `${pd(Math.floor(sec / 86400))} روز`;
};

const fmtBytes = (bytes?: number) => {
  if (!bytes) return '۰';
  const units = ['بایت', 'کیلوبایت', 'مگابایت', 'گیگابایت'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${pd((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
};

const SOURCE_LABELS: Record<string, string> = {
  backend: 'بک‌اند', web: 'وب‌سایت', admin: 'پنل ادمین',
  mobile: 'اپ موبایل', watchdog: 'نگهبان', cron: 'کرون', worker: 'ورکر',
};

const LEVEL_COLORS: Record<string, 'error' | 'warning' | 'info'> = {
  fatal: 'error', error: 'error', warning: 'warning',
};

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'بحرانی', error: 'خطا', warning: 'هشدار', info: 'اطلاع',
};

/**
 * کارت عدد بزرگ — واحد تکرارشوندهٔ نمای کلی.
 *
 * رنگ فقط وقتی ظاهر می‌شود که عدد معنای بد داشته باشد. داشبوردی که همیشه
 * رنگارنگ است، هیچ‌چیز را برجسته نمی‌کند و آدم یاد می‌گیرد نادیده‌اش بگیرد.
 */
const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: 'ok' | 'warn' | 'bad' | 'neutral';
}> = ({ label, value, hint, tone = 'neutral' }) => {
  const colors = { ok: '#2e7d32', warn: '#ed6c02', bad: '#d32f2f', neutral: 'inherit' };
  return (
    <Paper sx={{ p: 2, minWidth: 150, flex: '1 1 150px', borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700, color: colors[tone] }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
  );
};

/** نشانگر سلامت یک سرویس زیرساختی. */
const ServicePill: React.FC<{ name: string; check?: { ok: boolean; latencyMs?: number; error?: string } }> = ({ name, check }) => {
  if (!check) return <Chip size="small" variant="outlined" label={`${name}: غیرفعال`} />;
  return (
    <Tooltip title={check.error || `زمان پاسخ: ${check.latencyMs ?? '—'}ms`}>
      <Chip
        size="small"
        color={check.ok ? 'success' : 'error'}
        icon={check.ok ? <CheckCircleIcon /> : <ErrorIcon />}
        label={`${name} ${check.ok ? `· ${pd(check.latencyMs ?? 0)}ms` : '· قطع'}`}
      />
    </Tooltip>
  );
};

/**
 * نمودار میله‌ای سبک — با div خالص، بدون کتابخانهٔ چارت.
 * برای «حس روند» کافی است و چند صد کیلوبایت به بیلد اضافه نمی‌کند.
 */
const MiniChart: React.FC<{
  data: Array<{ label: string; value: number; bad?: number }>;
  height?: number;
  unit?: string;
}> = ({ data, height = 70, unit = '' }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  if (data.length === 0) return <Typography variant="caption" color="text.secondary">داده‌ای نیست</Typography>;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height, mt: 1 }}>
      {data.map((d, i) => (
        <Tooltip key={i} title={`${d.label}: ${pd(d.value)}${unit}${d.bad ? ` · ${pd(d.bad)} خطا` : ''}`}>
          <Box sx={{ flex: 1, minWidth: 2, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
            {!!d.bad && (
              <Box sx={{ bgcolor: 'error.main', height: `${Math.max(4, (d.bad / max) * 100)}%`, borderRadius: '2px 2px 0 0' }} />
            )}
            <Box sx={{ bgcolor: 'primary.main', opacity: 0.75, height: `${Math.max(2, (d.value / max) * 100)}%`, borderRadius: d.bad ? 0 : '2px 2px 0 0' }} />
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// صفحهٔ اصلی
// ---------------------------------------------------------------------------

const MonitoringCenter: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [liveSystem, setLiveSystem] = useState<SystemSnapshot | null>(null);
  const [liveHttp, setLiveHttp] = useState<HttpMetrics | null>(null);
  const [streaming, setStreaming] = useState(true);
  const [connected, setConnected] = useState(false);

  const stopStream = useRef<(() => void) | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadOverview = useCallback(async () => {
    try {
      const data = await monitoringService.getOverview();
      setOverview(data);
      setLiveSystem(data.system);
      setLiveHttp(data.http);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'دریافت وضعیت سیستم ناموفق بود');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadOverview(); }, [loadOverview]);

  // استریم زنده با اتصال مجدد و عقب‌نشینی نمایی.
  // اگر بک‌اند در حال ری‌استارت باشد، صفحه نباید هر ثانیه اتصال جدید باز کند —
  // دقیقاً وقتی سرور ضعیف است، نباید بیشتر فشار بیاوریم.
  useEffect(() => {
    if (!streaming) {
      stopStream.current?.();
      stopStream.current = null;
      setConnected(false);
      return;
    }

    let attempt = 0;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      stopStream.current = monitoringService.openLiveStream(
        (payload) => {
          attempt = 0;
          setConnected(true);
          if (payload.system) setLiveSystem(payload.system as SystemSnapshot);
          if (payload.http) setLiveHttp(payload.http);
        },
        () => {
          setConnected(false);
          attempt = Math.min(attempt + 1, 5);
          retryTimer.current = setTimeout(connect, 1000 * Math.pow(2, attempt));
        }
      );
    };
    connect();

    return () => {
      cancelled = true;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      stopStream.current?.();
      stopStream.current = null;
    };
  }, [streaming]);

  const system = liveSystem || overview?.system || null;
  const http = liveHttp || overview?.http || null;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  }

  const openIncidents = overview?.incidents.list || [];
  const criticalOpen = openIncidents.filter((i) => i.severity === 'critical');

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <MonitorHeartIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>مرکز پایش</Typography>
        <Chip
          size="small"
          color={connected ? 'success' : 'default'}
          variant={connected ? 'filled' : 'outlined'}
          label={connected ? 'زنده' : 'آفلاین'}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title={streaming ? 'توقف به‌روزرسانی زنده' : 'شروع به‌روزرسانی زنده'}>
          <IconButton size="small" onClick={() => setStreaming((s) => !s)}>
            {streaming ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="بارگذاری مجدد">
          <IconButton size="small" onClick={() => void loadOverview()}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {criticalOpen.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
          <b>{pd(criticalOpen.length)} مشکل بحرانی باز است:</b>{' '}
          {criticalOpen.map((i) => i.title).join(' · ')}
        </Alert>
      )}
      {overview && !overview.alerting.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          سیستم هشدار ایمیلی <b>فعال نیست</b>. تا وقتی <code>ALERT_EMAIL_TO</code> و تنظیمات SMTP
          پر نشوند، در صورت بروز مشکل ایمیلی دریافت نخواهید کرد.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab icon={<MonitorHeartIcon />} iconPosition="start" label="نمای کلی" />
        <Tab icon={<BugReportIcon />} iconPosition="start" label="خطاها" />
        <Tab icon={<NotificationsActiveIcon />} iconPosition="start" label="حادثه‌ها" />
        <Tab icon={<TimelineIcon />} iconPosition="start" label="روند و آپتایم" />
        <Tab icon={<TerminalIcon />} iconPosition="start" label="لاگ زنده" />
      </Tabs>

      {tab === 0 && <OverviewTab overview={overview} system={system} http={http} />}
      {tab === 1 && <ErrorsTab />}
      {tab === 2 && <IncidentsTab />}
      {tab === 3 && <TrendsTab />}
      {tab === 4 && <LogsTab />}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// تب ۱ — نمای کلی
// ---------------------------------------------------------------------------

const OverviewTab: React.FC<{
  overview: Overview | null;
  system: SystemSnapshot | null;
  http: HttpMetrics | null;
}> = ({ overview, system, http }) => {
  const [testing, setTesting] = useState(false);

  const sendTest = async () => {
    setTesting(true);
    try {
      const result = await monitoringService.sendTestAlert();
      toast.success(`ایمیل تست به ${result.recipients.join('، ')} فرستاده شد`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'ارسال ایمیل تست ناموفق بود');
    } finally {
      setTesting(false);
    }
  };

  const heapPercent = system?.heapTotalMb ? Math.round((system.heapUsedMb / system.heapTotalMb) * 100) : 0;
  const err5xxRatio = http?.last5min.ratio || 0;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <StatCard
          label="آپتایم پروسه"
          value={fmtDuration(system?.uptimeSec)}
          hint={system?.bootedAt ? `از ${fmtDate(system.bootedAt)}` : undefined}
        />
        <StatCard
          label="درخواست‌های باز"
          value={fmtNum(http?.inFlight)}
          tone={(http?.inFlight || 0) > 100 ? 'warn' : 'neutral'}
          hint="در حال پردازش همین لحظه"
        />
        <StatCard
          label="نرخ خطای ۵ دقیقه"
          value={`${pd((err5xxRatio * 100).toFixed(1))}٪`}
          tone={err5xxRatio > 0.05 ? 'bad' : err5xxRatio > 0.01 ? 'warn' : 'ok'}
          hint={`${fmtNum(http?.last5min.errors5xx)} از ${fmtNum(http?.last5min.requests)} درخواست`}
        />
        <StatCard
          label="خطاهای ۲۴ ساعت"
          value={fmtNum(overview?.errors.last24h)}
          tone={(overview?.errors.lastHour || 0) > 0 ? 'warn' : 'ok'}
          hint={`${fmtNum(overview?.errors.lastHour)} در ساعت اخیر`}
        />
        <StatCard
          label="حادثه‌های باز"
          value={fmtNum(overview?.incidents.open)}
          tone={(overview?.incidents.open || 0) > 0 ? 'bad' : 'ok'}
        />
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>سلامت زیرساخت</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <ServicePill name="MongoDB" check={system?.checks?.mongodb} />
          <ServicePill name="Redis" check={system?.checks?.redis} />
          <ServicePill name="Elasticsearch" check={system?.checks?.elasticsearch} />
          <Chip
            size="small"
            color={(system?.eventLoopP99Ms || 0) > 1000 ? 'error' : 'default'}
            label={`تأخیر حلقهٔ رویداد: ${pd(system?.eventLoopP99Ms ?? 0)}ms`}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 220px' }}>
            <Typography variant="caption" color="text.secondary">
              حافظهٔ پروسه — {pd(system?.heapUsedMb ?? 0)} از {pd(system?.heapTotalMb ?? 0)} مگابایت
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(heapPercent, 100)}
              color={heapPercent > 90 ? 'error' : heapPercent > 75 ? 'warning' : 'primary'}
              sx={{ mt: 0.5, height: 7, borderRadius: 4 }}
            />
          </Box>

          {system?.diskUsedPercent != null && (
            <Box sx={{ flex: '1 1 220px' }}>
              <Typography variant="caption" color="text.secondary">
                دیسک سرور — {pd(system.diskUsedPercent)}٪ اشغال
              </Typography>
              <LinearProgress
                variant="determinate"
                value={system.diskUsedPercent}
                color={system.diskUsedPercent > 90 ? 'error' : system.diskUsedPercent > 80 ? 'warning' : 'primary'}
                sx={{ mt: 0.5, height: 7, borderRadius: 4 }}
              />
            </Box>
          )}

          {system?.load1 != null && system?.cores != null && (
            <Box sx={{ flex: '1 1 220px' }}>
              <Typography variant="caption" color="text.secondary">
                بار CPU — {pd(system.load1)} روی {pd(system.cores)} هسته
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min((system.load1 / system.cores) * 100, 100)}
                color={system.load1 / system.cores > 2 ? 'error' : system.load1 / system.cores > 1 ? 'warning' : 'primary'}
                sx={{ mt: 0.5, height: 7, borderRadius: 4 }}
              />
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {system?.hostname && `میزبان ${system.hostname} · `}
          {system?.nodeVersion && `Node ${system.nodeVersion} · `}
          {system?.pid && `PID ${pd(system.pid)} · `}
          آخرین بررسی {fmtTime(system?.at)}
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>ترافیک یک ساعت اخیر</Typography>
        <Typography variant="caption" color="text.secondary">
          میله‌های قرمز = خطای سرور (۵xx) در همان دقیقه
        </Typography>
        <MiniChart
          height={90}
          data={(http?.timeline || []).map((t) => ({ label: fmtTime(t.at), value: t.requests, bad: t.errors5xx }))}
        />
        <Stack direction="row" spacing={3} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Typography variant="caption">کل درخواست‌ها: <b>{fmtNum(http?.totals.requests)}</b></Typography>
          <Typography variant="caption">میانگین پاسخ: <b>{fmtNum(http?.totals.avgMs)}ms</b></Typography>
          <Typography variant="caption">خطای ۵xx: <b>{fmtNum(http?.totals.errors5xx)}</b></Typography>
          <Typography variant="caption">خطای ۴xx: <b>{fmtNum(http?.totals.errors4xx)}</b></Typography>
        </Stack>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, borderRadius: 2, flex: '1 1 380px' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>کندترین مسیرها</Typography>
          <RouteTable rows={http?.slowestRoutes || []} metric="p95Ms" />
        </Paper>
        <Paper sx={{ p: 2, borderRadius: 2, flex: '1 1 380px' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>مسیرهای خطادار</Typography>
          <RouteTable rows={http?.failingRoutes || []} metric="errors5xx" />
        </Paper>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>آخرین خطاها</Typography>
        {(overview?.errors.top || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            ✅ در ۲۴ ساعت گذشته خطای حل‌نشده‌ای ثبت نشده است.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableBody>
                {overview!.errors.top.map((e) => (
                  <TableRow key={e.fingerprint}>
                    <TableCell sx={{ width: 90 }}>
                      <Chip size="small" label={SOURCE_LABELS[e.source] || e.source} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.type}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        {e.message}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: 110, textAlign: 'center' }}>
                      <Chip size="small" color="error" label={`${fmtNum(e.count)} بار`} />
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>
                      <Typography variant="caption">{fmtDate(e.lastSeenAt)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>سیستم هشدار</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={testing ? <CircularProgress size={14} /> : <SendIcon />}
            disabled={testing || !overview?.alerting.enabled}
            onClick={sendTest}
          >
            ارسال ایمیل تست
          </Button>
        </Box>
        <Stack spacing={0.5}>
          <Typography variant="body2" component="div">
            وضعیت:{' '}
            <Chip size="small" color={overview?.alerting.enabled ? 'success' : 'default'}
              label={overview?.alerting.enabled ? 'فعال' : 'غیرفعال'} />
          </Typography>
          <Typography variant="body2">گیرندگان: <b>{overview?.alerting.recipients.join('، ') || '—'}</b></Typography>
          <Typography variant="body2">
            یادآوری هر <b>{pd(overview?.alerting.reminderMinutes ?? 0)}</b> دقیقه ·
            سقف <b>{pd(overview?.alerting.maxEmailsPerHour ?? 0)}</b> ایمیل در ساعت
          </Typography>
          {!!overview?.alerting.pendingCount && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {pd(overview.alerting.pendingCount)} هشدار در صف ارسال مانده — یعنی SMTP در
              دسترس نبوده. سیستم خودش دوباره تلاش می‌کند.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            لاگ‌ها: {fmtBytes(overview?.logs.totalBytes)} در {pd(overview?.logs.fileCount ?? 0)} فایل
            {overview?.logs.dir ? ` · ${overview.logs.dir}` : ''}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
};

const RouteTable: React.FC<{ rows: RouteMetric[]; metric: 'p95Ms' | 'errors5xx' }> = ({ rows, metric }) => {
  if (rows.length === 0) {
    return <Typography variant="body2" color="text.secondary">داده‌ای ثبت نشده است.</Typography>;
  }
  return (
    <TableContainer sx={{ maxHeight: 300 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>مسیر</TableCell>
            <TableCell align="center">تعداد</TableCell>
            <TableCell align="center">{metric === 'p95Ms' ? 'p95' : 'خطا'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, 10).map((r) => (
            <TableRow key={r.route}>
              <TableCell sx={{ direction: 'ltr', textAlign: 'left', fontFamily: 'monospace', fontSize: 11.5 }}>
                {r.route}
              </TableCell>
              <TableCell align="center">{fmtNum(r.requests)}</TableCell>
              <TableCell align="center">
                {metric === 'p95Ms'
                  ? <Chip size="small" color={r.p95Ms > 3000 ? 'error' : r.p95Ms > 1000 ? 'warning' : 'default'} label={`${fmtNum(r.p95Ms)}ms`} />
                  : <Chip size="small" color="error" label={fmtNum(r.errors5xx)} />}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ---------------------------------------------------------------------------
// تب ۲ — خطاها
// ---------------------------------------------------------------------------

const ErrorsTab: React.FC = () => {
  const [rows, setRows] = useState<ErrorGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ status: 'open', source: '', level: '', search: '', sort: 'lastSeenAt' });
  const [detail, setDetail] = useState<ErrorGroupDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await monitoringService.listErrors({ ...filters, page, limit: 25 });
      setRows(res.items || []);
      setPages(res.pagination?.pages || 1);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'دریافت لیست خطاها ناموفق بود');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { void load(); }, [load]);

  const openDetail = async (id: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail(await monitoringService.getErrorDetail(id));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'دریافت جزئیات خطا ناموفق بود');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const changeStatus = async (id: string, status: ErrorStatus) => {
    try {
      await monitoringService.updateErrorStatus(id, status);
      toast.success(status === 'resolved' ? 'خطا بسته شد' : status === 'ignored' ? 'خطا نادیده گرفته شد' : 'خطا دوباره باز شد');
      setDetailOpen(false);
      void load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تغییر وضعیت ناموفق بود');
    }
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            size="small" select label="وضعیت" value={filters.status} sx={{ minWidth: 130 }}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, status: e.target.value })); }}
          >
            <MenuItem value="open">باز</MenuItem>
            <MenuItem value="resolved">بسته‌شده</MenuItem>
            <MenuItem value="ignored">نادیده‌گرفته</MenuItem>
          </TextField>
          <TextField
            size="small" select label="منبع" value={filters.source} sx={{ minWidth: 140 }}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, source: e.target.value })); }}
          >
            <MenuItem value="">همه</MenuItem>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </TextField>
          <TextField
            size="small" select label="سطح" value={filters.level} sx={{ minWidth: 120 }}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, level: e.target.value })); }}
          >
            <MenuItem value="">همه</MenuItem>
            <MenuItem value="fatal">بحرانی</MenuItem>
            <MenuItem value="error">خطا</MenuItem>
            <MenuItem value="warning">هشدار</MenuItem>
          </TextField>
          <TextField
            size="small" select label="مرتب‌سازی" value={filters.sort} sx={{ minWidth: 150 }}
            onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
          >
            <MenuItem value="lastSeenAt">تازه‌ترین</MenuItem>
            <MenuItem value="count">پرتکرارترین</MenuItem>
            <MenuItem value="affectedUserCount">بیشترین کاربر</MenuItem>
            <MenuItem value="firstSeenAt">قدیمی‌ترین</MenuItem>
          </TextField>
          <TextField
            size="small" label="جست‌وجو در پیام/محل" sx={{ minWidth: 240, flex: 1 }}
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); void load(); } }}
          />
          <IconButton onClick={() => void load()}><RefreshIcon /></IconButton>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
            <Typography color="text.secondary">خطایی با این فیلترها وجود ندارد.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>منبع</TableCell>
                  <TableCell>خطا</TableCell>
                  <TableCell align="center">تکرار</TableCell>
                  <TableCell align="center">کاربر</TableCell>
                  <TableCell>آخرین بار</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((e) => (
                  <TableRow key={e._id} hover sx={{ cursor: 'pointer' }} onClick={() => void openDetail(e._id)}>
                    <TableCell sx={{ width: 100 }}>
                      <Chip size="small" variant="outlined" label={SOURCE_LABELS[e.source] || e.source} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                        <Chip size="small" color={LEVEL_COLORS[e.level] || 'default'} label={e.level} sx={{ height: 18, fontSize: 10 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.type}</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                        {e.message}
                      </Typography>
                      {e.culprit && (
                        <Typography variant="caption" sx={{ direction: 'ltr', display: 'block', fontFamily: 'monospace', fontSize: 10.5, color: 'text.disabled' }}>
                          {e.culprit}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 90 }}>
                      <Chip size="small" color={e.count > 100 ? 'error' : 'default'} label={fmtNum(e.count)} />
                    </TableCell>
                    <TableCell align="center" sx={{ width: 70 }}>{fmtNum(e.affectedUserCount)}</TableCell>
                    <TableCell sx={{ width: 140 }}>
                      <Typography variant="caption">{fmtDate(e.lastSeenAt)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
            <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} size="small" />
          </Box>
        )}
      </Paper>

      {detailOpen && (
        <ErrorDetailDialog
          detail={detail}
          loading={detailLoading}
          onClose={() => setDetailOpen(false)}
          onChangeStatus={changeStatus}
        />
      )}
    </Stack>
  );
};

const ErrorDetailDialog: React.FC<{
  detail: ErrorGroupDetail | null;
  loading: boolean;
  onClose: () => void;
  onChangeStatus: (id: string, status: ErrorStatus) => void;
}> = ({ detail, loading, onClose, onChangeStatus }) => {
  const [sampleIndex, setSampleIndex] = useState(0);

  useEffect(() => { setSampleIndex(0); }, [detail?._id]);

  const sample = detail?.samples?.[sampleIndex];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      {loading || !detail ? (
        <DialogContent sx={{ textAlign: 'center', p: 6 }}><CircularProgress /></DialogContent>
      ) : (
        <>
          <DialogTitle sx={{ pr: 6 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" color={LEVEL_COLORS[detail.level] || 'default'} label={detail.level} />
              <Chip size="small" variant="outlined" label={SOURCE_LABELS[detail.source] || detail.source} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{detail.type}</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
              {detail.message}
            </Typography>
            <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, left: 8 }} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <StatCard label="تکرار کل" value={fmtNum(detail.count)} />
              <StatCard label="کاربران متأثر" value={fmtNum(detail.affectedUserCount)} />
              <StatCard label="اولین بار" value={<Typography variant="body2">{fmtDate(detail.firstSeenAt)}</Typography>} />
              <StatCard label="آخرین بار" value={<Typography variant="body2">{fmtDate(detail.lastSeenAt)}</Typography>} />
            </Box>

            {detail.culprit && (
              <Typography variant="body2" sx={{ mb: 2, direction: 'ltr', fontFamily: 'monospace', fontSize: 12, bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                {detail.culprit}
              </Typography>
            )}

            {Object.keys(detail.dailyCounts || {}).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">روند روزانه</Typography>
                <MiniChart
                  data={Object.entries(detail.dailyCounts)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .slice(-30)
                    .map(([date, count]) => ({ label: date, value: count }))}
                />
              </Box>
            )}

            {detail.samples?.length > 0 && (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>نمونه‌های اخیر</Typography>
                  {detail.samples.map((s, i) => (
                    <Chip
                      key={i}
                      size="small"
                      color={i === sampleIndex ? 'primary' : 'default'}
                      variant={i === sampleIndex ? 'filled' : 'outlined'}
                      label={fmtTime(s.at)}
                      onClick={() => setSampleIndex(i)}
                    />
                  ))}
                </Stack>

                {sample && (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={0.5} sx={{ mb: 1 }}>
                      {sample.route && <Typography variant="caption">مسیر: <b style={{ direction: 'ltr', display: 'inline-block' }}>{sample.route}</b></Typography>}
                      {sample.statusCode && <Typography variant="caption">کد وضعیت: <b>{pd(sample.statusCode)}</b></Typography>}
                      {sample.requestId && (
                        <Typography variant="caption">
                          شناسهٔ درخواست: <b style={{ direction: 'ltr', display: 'inline-block', fontFamily: 'monospace' }}>{sample.requestId}</b>
                          {' '}— با همین شناسه در تب «لاگ زنده» کل مسیر این درخواست را ببینید
                        </Typography>
                      )}
                      {sample.ip && <Typography variant="caption">IP: <b style={{ direction: 'ltr', display: 'inline-block' }}>{sample.ip}</b></Typography>}
                      {sample.release && <Typography variant="caption">نسخه: <b>{sample.release}</b></Typography>}
                      {sample.userAgent && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', wordBreak: 'break-all' }}>
                          {sample.userAgent}
                        </Typography>
                      )}
                    </Stack>

                    {sample.stack && (
                      <Box
                        component="pre"
                        sx={{
                          direction: 'ltr', textAlign: 'left', fontSize: 11, m: 0, p: 1.5,
                          bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1,
                          overflowX: 'auto', maxHeight: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}
                      >
                        {sample.stack}
                      </Box>
                    )}

                    {sample.context && Object.keys(sample.context).length > 0 && (
                      <Box
                        component="pre"
                        sx={{
                          direction: 'ltr', textAlign: 'left', fontSize: 11, mt: 1, mb: 0, p: 1.5,
                          bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto', maxHeight: 200,
                        }}
                      >
                        {JSON.stringify(sample.context, null, 2)}
                      </Box>
                    )}
                  </Paper>
                )}
              </>
            )}
          </DialogContent>

          <DialogActions>
            {detail.status !== 'open' && (
              <Button onClick={() => onChangeStatus(detail._id, 'open')}>بازکردن دوباره</Button>
            )}
            {detail.status !== 'ignored' && (
              <Button color="inherit" onClick={() => onChangeStatus(detail._id, 'ignored')}>نادیده بگیر</Button>
            )}
            {detail.status !== 'resolved' && (
              <Button variant="contained" color="success" onClick={() => onChangeStatus(detail._id, 'resolved')}>
                حل شد
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// تب ۳ — حادثه‌ها
// ---------------------------------------------------------------------------

const IncidentsTab: React.FC = () => {
  const [rows, setRows] = useState<AlertIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await monitoringService.listIncidents({ status: status || undefined, page, limit: 25 });
      setRows(res.items || []);
      setPages(res.pagination?.pages || 1);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'دریافت حادثه‌ها ناموفق بود');
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  useEffect(() => { void load(); }, [load]);

  const acknowledge = async (id: string) => {
    try {
      await monitoringService.acknowledgeIncident(id);
      toast.success('حادثه تأیید شد');
      void load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تأیید حادثه ناموفق بود');
    }
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            size="small" select label="وضعیت" value={status} sx={{ minWidth: 160 }}
            onChange={(e) => { setPage(1); setStatus(e.target.value); }}
          >
            <MenuItem value="">همه</MenuItem>
            <MenuItem value="firing">در حال وقوع</MenuItem>
            <MenuItem value="acknowledged">تأییدشده</MenuItem>
            <MenuItem value="resolved">برطرف‌شده</MenuItem>
          </TextField>
          <IconButton onClick={() => void load()}><RefreshIcon /></IconButton>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
            <Typography color="text.secondary">حادثه‌ای ثبت نشده است.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>شدت</TableCell>
                  <TableCell>عنوان</TableCell>
                  <TableCell>شروع</TableCell>
                  <TableCell align="center">مدت</TableCell>
                  <TableCell align="center">ایمیل</TableCell>
                  <TableCell align="center">وضعیت</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((i) => {
                  const durationSec = i.durationSec ?? (Date.now() - new Date(i.firstFiredAt).getTime()) / 1000;
                  const emailOk = i.notifications?.some((n) => n.ok);
                  const emailFailed = (i.notifications?.length || 0) > 0 && !emailOk;
                  return (
                    <TableRow key={i._id} hover>
                      <TableCell sx={{ width: 90 }}>
                        <Chip
                          size="small"
                          color={i.severity === 'critical' ? 'error' : i.severity === 'error' ? 'warning' : 'default'}
                          label={SEVERITY_LABELS[i.severity] || i.severity}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{i.summary}</Typography>
                      </TableCell>
                      <TableCell sx={{ width: 145 }}>
                        <Typography variant="caption">{fmtDate(i.firstFiredAt)}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 120 }}>
                        <Typography variant="caption">{fmtDuration(durationSec)}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 80 }}>
                        {emailOk ? (
                          <Tooltip title="ایمیل هشدار فرستاده شد"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>
                        ) : emailFailed ? (
                          <Tooltip title={i.notifications.find((n) => !n.ok)?.error || 'ارسال ناموفق'}>
                            <WarningAmberIcon color="error" fontSize="small" />
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 130 }}>
                        {i.status === 'resolved' ? (
                          <Chip size="small" color="success" label="برطرف شد" />
                        ) : i.status === 'acknowledged' ? (
                          <Chip size="small" color="info" label="تأییدشده" />
                        ) : (
                          <Button size="small" variant="outlined" onClick={() => acknowledge(i._id)}>تأیید</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
            <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} size="small" />
          </Box>
        )}
      </Paper>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// تب ۴ — روند و آپتایم
// ---------------------------------------------------------------------------

const TrendsTab: React.FC = () => {
  const [hours, setHours] = useState(24);
  const [days, setDays] = useState(30);
  const [health, setHealth] = useState<{ samples: HealthSample[]; restarts: string[] } | null>(null);
  const [uptime, setUptime] = useState<UptimeReport | null>(null);
  const [errorStats, setErrorStats] = useState<{
    daily: Array<{ date: string; count: number }>;
    bySource: Array<{ _id: string; groups: number; events: number }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      monitoringService.getHealthHistory(hours),
      monitoringService.getUptime(days),
      monitoringService.getErrorStats(Math.min(days, 30)),
    ])
      .then(([h, u, e]) => {
        if (!alive) return;
        setHealth({ samples: h.samples, restarts: h.restarts });
        setUptime(u);
        setErrorStats(e);
      })
      .catch((err: any) => toast.error(err?.response?.data?.message || 'دریافت روندها ناموفق بود'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [hours, days]);

  const latencySeries = useMemo(
    () => (health?.samples || []).map((s) => ({ label: fmtTime(s.at), value: s.http?.p95Ms || 0, bad: s.http?.errors5xx || 0 })),
    [health]
  );
  const memorySeries = useMemo(
    () => (health?.samples || []).map((s) => ({ label: fmtTime(s.at), value: s.heapUsedMb })),
    [health]
  );
  const loopSeries = useMemo(
    () => (health?.samples || []).map((s) => ({ label: fmtTime(s.at), value: s.eventLoopP99Ms })),
    [health]
  );

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField size="small" select label="بازهٔ نمودارها" value={hours} sx={{ minWidth: 140 }}
            onChange={(e) => setHours(Number(e.target.value))}>
            <MenuItem value={6}>۶ ساعت</MenuItem>
            <MenuItem value={24}>۲۴ ساعت</MenuItem>
            <MenuItem value={72}>۳ روز</MenuItem>
            <MenuItem value={168}>۷ روز</MenuItem>
          </TextField>
          <TextField size="small" select label="بازهٔ آپتایم" value={days} sx={{ minWidth: 140 }}
            onChange={(e) => setDays(Number(e.target.value))}>
            <MenuItem value={7}>۷ روز</MenuItem>
            <MenuItem value={30}>۳۰ روز</MenuItem>
            <MenuItem value={90}>۹۰ روز</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          آپتایم {pd(days)} روز اخیر
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <StatCard
            label="درصد آپتایم"
            value={`${pd((uptime?.uptimePercent ?? 100).toFixed(3))}٪`}
            tone={(uptime?.uptimePercent ?? 100) >= 99.9 ? 'ok' : (uptime?.uptimePercent ?? 100) >= 99 ? 'warn' : 'bad'}
          />
          <StatCard label="مدت کل قطعی" value={fmtDuration(uptime?.downtimeSec)} tone={uptime?.downtimeSec ? 'warn' : 'ok'} />
          <StatCard label="تعداد قطعی" value={fmtNum(uptime?.incidentCount)} />
          <StatCard label="ری‌استارت پروسه" value={fmtNum(health?.restarts.length)} hint={`در ${pd(hours)} ساعت اخیر`} />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          ⓘ این عدد از روی حادثه‌های <b>بحرانی</b> ثبت‌شده حساب می‌شود. اگر کل سرور خوابیده باشد،
          بک‌اند نمی‌تواند چیزی ثبت کند — سنجش قطعی واقعی کار «نگهبان مستقل» است.
        </Typography>

        {(uptime?.timeline || []).length === 0 ? (
          <Typography variant="body2" color="success.main">✅ در این بازه هیچ قطعی بحرانی ثبت نشده است.</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow><TableCell>مشکل</TableCell><TableCell>شروع</TableCell><TableCell>پایان</TableCell><TableCell align="center">مدت</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {uptime!.timeline.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.title}</TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(t.startedAt)}</Typography></TableCell>
                    <TableCell>
                      {t.ongoing ? <Chip size="small" color="error" label="ادامه دارد" /> : <Typography variant="caption">{fmtDate(t.endedAt)}</Typography>}
                    </TableCell>
                    <TableCell align="center"><Typography variant="caption">{fmtDuration(t.durationSec)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, borderRadius: 2, flex: '1 1 340px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>تأخیر پاسخ (p95)</Typography>
          <Typography variant="caption" color="text.secondary">میله‌های قرمز = خطای ۵xx</Typography>
          <MiniChart data={latencySeries} height={90} unit="ms" />
        </Paper>
        <Paper sx={{ p: 2, borderRadius: 2, flex: '1 1 340px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>حافظهٔ پروسه</Typography>
          <Typography variant="caption" color="text.secondary">افت ناگهانی = ری‌استارت</Typography>
          <MiniChart data={memorySeries} height={90} unit="MB" />
        </Paper>
        <Paper sx={{ p: 2, borderRadius: 2, flex: '1 1 340px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>تأخیر حلقهٔ رویداد</Typography>
          <Typography variant="caption" color="text.secondary">بالا رفتن = پروسه بلاک شده</Typography>
          <MiniChart data={loopSeries} height={90} unit="ms" />
        </Paper>
        <Paper sx={{ p: 2, borderRadius: 2, flex: '1 1 340px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>خطاهای روزانه</Typography>
          <Typography variant="caption" color="text.secondary">
            {(errorStats?.bySource || []).map((s) => `${SOURCE_LABELS[s._id] || s._id}: ${pd(s.events)}`).join(' · ') || '—'}
          </Typography>
          <MiniChart data={(errorStats?.daily || []).map((d) => ({ label: d.date, value: d.count }))} height={90} />
        </Paper>
      </Box>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// تب ۵ — لاگ زنده
// ---------------------------------------------------------------------------

const LEVEL_STYLE: Record<string, { color: string; label: string }> = {
  error: { color: '#f85149', label: 'ERROR' },
  warn: { color: '#d29922', label: 'WARN' },
  info: { color: '#58a6ff', label: 'INFO' },
  http: { color: '#8b949e', label: 'HTTP' },
  debug: { color: '#8b949e', label: 'DEBUG' },
  raw: { color: '#8b949e', label: 'RAW' },
};

const LogsTab: React.FC = () => {
  const [channel, setChannel] = useState('app');
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [requestId, setRequestId] = useState('');
  const [lines, setLines] = useState<LogLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [follow, setFollow] = useState(true);
  const [file, setFile] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await monitoringService.tailLogs({
        channel,
        level: level || undefined,
        search: search || undefined,
        requestId: requestId || undefined,
        limit: 300,
      });
      setLines(res.lines);
      setFile(res.file);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خواندن لاگ ناموفق بود');
    } finally {
      setLoading(false);
    }
  }, [channel, level, search, requestId]);

  useEffect(() => { void load(); }, [load]);

  // دنبال‌کردن زنده با پولینگ ۵ ثانیه‌ای — عمداً استریم نیست: این تب ممکن است
  // ساعت‌ها باز بماند و یک اتصال همیشه‌باز که مدام لاگ می‌فرستد، هزینه‌اش از
  // فایده‌اش بیشتر است.
  useEffect(() => {
    if (!follow) return;
    const timer = setInterval(() => void load(), 5000);
    return () => clearInterval(timer);
  }, [follow, load]);

  useEffect(() => {
    if (follow) bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [lines, follow]);

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField size="small" select label="کانال" value={channel} sx={{ minWidth: 150 }}
            onChange={(e) => setChannel(e.target.value)}>
            <MenuItem value="app">همه (app)</MenuItem>
            <MenuItem value="error">فقط خطاها</MenuItem>
            <MenuItem value="http">دسترسی HTTP</MenuItem>
          </TextField>
          <TextField size="small" select label="سطح" value={level} sx={{ minWidth: 120 }}
            onChange={(e) => setLevel(e.target.value)}>
            <MenuItem value="">همه</MenuItem>
            <MenuItem value="error">error</MenuItem>
            <MenuItem value="warn">warn</MenuItem>
            <MenuItem value="info">info</MenuItem>
            <MenuItem value="debug">debug</MenuItem>
          </TextField>
          <TextField size="small" label="جست‌وجو در متن" sx={{ minWidth: 200, flex: 1 }}
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void load(); }} />
          <TextField size="small" label="شناسهٔ درخواست" sx={{ minWidth: 200 }}
            placeholder="x-request-id"
            value={requestId} onChange={(e) => setRequestId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void load(); }} />
          <Button size="small" variant={follow ? 'contained' : 'outlined'} onClick={() => setFollow((f) => !f)}
            startIcon={follow ? <PauseIcon /> : <PlayArrowIcon />}>
            {follow ? 'دنبال‌کردن' : 'متوقف'}
          </Button>
          <IconButton onClick={() => void load()} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Stack>
        {file && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            فایل: <b style={{ direction: 'ltr', display: 'inline-block' }}>{file}</b> ·
            {' '}{pd(lines.length)} خط
          </Typography>
        )}
      </Paper>

      <Paper
        sx={{
          borderRadius: 2, bgcolor: '#0d1117', color: '#c9d1d9',
          p: 1.5, maxHeight: '65vh', overflow: 'auto',
          fontFamily: 'Consolas, Menlo, monospace', fontSize: 11.5, direction: 'ltr', textAlign: 'left',
        }}
      >
        {lines.length === 0 ? (
          <Typography sx={{ color: '#8b949e', p: 2, textAlign: 'center', direction: 'rtl', fontFamily: 'inherit' }}>
            خطی با این فیلترها پیدا نشد.
          </Typography>
        ) : (
          lines.map((l, i) => {
            const style = LEVEL_STYLE[String(l.level || 'info').toLowerCase()] || LEVEL_STYLE.info;
            const extras = Object.entries(l).filter(
              ([k]) => !['timestamp', 'level', 'message', 'stack', 'service', 'channel'].includes(k)
            );
            return (
              <Box key={i} sx={{ py: 0.4, borderBottom: '1px solid #1b2129', '&:hover': { bgcolor: '#161b22' } }}>
                <Box component="span" sx={{ color: '#6e7681' }}>
                  {l.timestamp ? String(l.timestamp).slice(11, 23) : '—'}
                </Box>{' '}
                <Box component="span" sx={{ color: style.color, fontWeight: 700 }}>{style.label}</Box>{' '}
                <Box component="span">{String(l.message ?? '')}</Box>
                {extras.length > 0 && (
                  <Box sx={{ color: '#6e7681', pl: 2, fontSize: 10.5, wordBreak: 'break-all' }}>
                    {extras.map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join('  ')}
                  </Box>
                )}
                {l.stack && (
                  <Box component="pre" sx={{ color: '#ff7b72', m: 0, pl: 2, fontSize: 10.5, whiteSpace: 'pre-wrap' }}>
                    {String(l.stack)}
                  </Box>
                )}
              </Box>
            );
          })
        )}
        <div ref={bottomRef} />
      </Paper>
    </Stack>
  );
};

export default MonitoringCenter;
